import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box, Button, Typography, Card, CardContent, Container,
  Tooltip, IconButton, TextField, Stack, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, MenuItem,
  FormControl, InputLabel, Select, Checkbox, Chip,
  CircularProgress, FormControlLabel, Radio, RadioGroup,
  Popover, ListItemText, ListItemIcon, Divider, Avatar, Alert
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PhoneIcon from "@mui/icons-material/Phone";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import PersonIcon from "@mui/icons-material/Person";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import CancelIcon from "@mui/icons-material/Cancel";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import RepeatIcon from "@mui/icons-material/Repeat";
import SaveIcon from "@mui/icons-material/Save";
import VideocamIcon from "@mui/icons-material/Videocam";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Grid from '@mui/material/Grid2';
import MiniDrawer from "../../components/MiniDrawer.js";
import { useTheme } from '@mui/material/styles';
import Loader from "../../loader.js";
import { useAppointmentData } from "./AppointmentData.js";
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './AppointmentCalendar.css';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { useLocation, useNavigate } from 'react-router-dom';
import { countryCodes } from "../../constants/countryCodes.js";
import { appointmentErrorMessage } from "./appointmentErrors.js";
import { DEFAULT_SESSION_MINUTES, addMinutes, initialSlot, startChanged, validateTimes, workingHoursFor } from "./appointmentTimes.js";
import { calendarRequest } from '../../services/calendarService.js';
import AvailabilityDialog from '../../components/calendar/AvailabilityDialog.js';
import GoogleCalendarDialog from '../../components/calendar/GoogleCalendarDialog.js';
import useCalendarBlocks from '../../components/calendar/useCalendarBlocks.js';
import ClientPicker, { appointmentFieldsForPhoneChange } from "./ClientPicker.js";

import DeclinePackageDialog from './ClientPackages/DeclinePackageDialog';

// Setup the localizer for the calendar
const localizer = momentLocalizer(moment);

// Arrival status colors (matching Figma design)
const arrivalStatusColors = {
  'pending': '#14b8a6',   // teal-500
  'completed': '#65a30d', // lime-600
  'cancelled': '#ef4444', // red-500
};

const arrivalStatusLabels = {
  'pending': 'Upcoming',
  'completed': 'Completed',
  'cancelled': 'Cancelled',
};

// Helper to derive the display status for an appointment.
// Maps appointment_status + date to one of: 'cancelled', 'completed', 'pending'.
const getArrivalStatus = (appointment) => {
  const status = appointment.appointment_status;

  if (status === 'cancelled') {
    return 'cancelled';
  }

  // An appointment is considered over once its end time has passed
  const endTime = appointment.end
    ? new Date(appointment.end)
    : (appointment.appointment_endtime ? new Date(appointment.appointment_endtime) : null);
  const isPast = endTime ? endTime.getTime() < Date.now() : false;

  if (status === 'completed' || isPast) {
    return 'completed';
  }

  return 'pending';
};

// CustomToolbar has no dependencies on outer component scope — defined outside to keep a stable reference
const CustomToolbar = (toolbar) => {
  const goToBack = () => toolbar.onNavigate('PREV');
  const goToNext = () => toolbar.onNavigate('NEXT');
  const goToCurrent = () => toolbar.onNavigate('TODAY');

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      alignItems: { xs: 'stretch', md: 'center' },
      justifyContent: 'space-between',
      gap: 2,
      mb: 3
    }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton onClick={goToBack} size="small" sx={{ '&:hover': { bgcolor: '#f3f4f6' } }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Button
          onClick={goToCurrent}
          variant="text"
          sx={{
            color: '#14b8a6',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': { bgcolor: '#f0fdfa' }
          }}
        >
          Today
        </Button>
        <IconButton onClick={goToNext} size="small" sx={{ '&:hover': { bgcolor: '#f3f4f6' } }}>
          <ArrowForwardIcon fontSize="small" />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600, ml: 1, fontSize: '1.1rem' }}>
          {toolbar.view === 'day'
            ? moment(toolbar.date).format('MMMM D, YYYY')
            : toolbar.view === 'week'
              ? `${moment(toolbar.date).startOf('week').format('MMM D')} – ${moment(toolbar.date).endOf('week').format('MMM D, YYYY')}`
              : moment(toolbar.date).format('MMMM YYYY')}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={0} sx={{ bgcolor: '#f3f4f6', borderRadius: '8px', p: '4px' }}>
        {['month', 'week', 'day'].map((v) => (
          <Button
            key={v}
            onClick={() => toolbar.onView(v)}
            sx={{
              textTransform: 'capitalize',
              fontWeight: 500,
              fontSize: '0.85rem',
              px: 2,
              py: 0.75,
              borderRadius: '6px',
              minWidth: 'auto',
              color: toolbar.view === v ? '#14b8a6' : '#6b7280',
              bgcolor: toolbar.view === v ? '#fff' : 'transparent',
              boxShadow: toolbar.view === v ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
              '&:hover': {
                bgcolor: toolbar.view === v ? '#fff' : 'rgba(0,0,0,0.04)',
                color: toolbar.view === v ? '#14b8a6' : '#111827',
              },
            }}
          >
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </Button>
        ))}
      </Stack>
    </Box>
  );
};

const Appointments = () => {
    const {
      loading,
      appointments,
      doctors,
      doctorsLoading,
      doctorsLoadError,
      selectedDoctorId,
      setSelectedDoctorId,
      showAppointmentForm,
      setShowAppointmentForm,
      isEditingAppointment,
      setIsEditingAppointment,
      currentAppointment,
      setCurrentAppointment,
      view,
      setView,
      filteredCategories,
      setFilteredCategories,
      selectedDate,
      setSelectedDate,
      showAppointmentDetails,
      setShowAppointmentDetails,
      selectedAppointment,
      setSelectedAppointment,
      fetchDoctors,
      fetchAppointments,
      saveDoctor,
      deleteDoctor,
      saveAppointment,
      deleteAppointment,
      // Rules-related (kept but commented out from UI)
      showRulesView,
      setShowRulesView,
      rules,
      selectedRuleId,
      setSelectedRuleId,
      isEditingRules,
      setIsEditingRules,
      editedRules,
      setEditedRules,
      selectedDoctorsForRule,
      setSelectedDoctorsForRule,
      ruleInstructions,
      setRuleInstructions,
      fetchRules,
      saveRule,
      deleteRule,
      handleRuleChange,
      saveRuleInstructions,
      currentOrg,

      setAppointments,
      setAlert,
      userDetails,
      teamMembersData,
      subscriptionActiveStatus,

      role,
      allOrgs,
      createPatientFromAppointment,
      clients,
      clientsLoading
    } = useAppointmentData();

    const [packageDeclineTarget, setPackageDeclineTarget] = useState(null);
    const closePackageDecline = () => { setPackageDeclineTarget(null); fetchAppointments(); };
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const tourMode = new URLSearchParams(location.search).get('tour');
    const tourDemoScreen = tourMode === 'preview'
      ? new URLSearchParams(location.search).get('demo')
      : null;
    const isVisualTour = Boolean(tourDemoScreen);

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [appointmentToDelete, setAppointmentToDelete] = useState(null);
    const [deleteRecurringOption, setDeleteRecurringOption] = useState('single');
    const [cancellationNote, setCancellationNote] = useState('');
    const [hasDoctorChanged, setHasDoctorChanged] = useState(false);
    // Why these live in the dialogs and not in the global toast: a toast that fires while a
    // modal is open is easy to miss and gone in seconds, and the failure it was reporting
    // (42501 on every save since 2026-09-02) also wiped the form. The reason now stays on
    // screen next to the fields it is about, for as long as the dialog is open.
    const [submitError, setSubmitError] = useState('');
    const [isSavingAppointment, setIsSavingAppointment] = useState(false);
    const [cancelError, setCancelError] = useState('');
    const [isCancellingAppointment, setIsCancellingAppointment] = useState(false);
    const blankFieldErrors = { patient_name: false, age: false };
    const [fieldErrors, setFieldErrors] = useState({ patient_name: false, age: false });

    const [patientCreationLoading, setPatientCreationLoading] = useState(false);
    const [showCreatePatientConfirm, setShowCreatePatientConfirm] = useState(false);
    const [showPatientCreationOptions, setShowPatientCreationOptions] = useState(false);
    const [selectedPatientTeamId, setSelectedPatientTeamId] = useState('');
    const [availabilityOpen, setAvailabilityOpen] = useState(false);
    const [googleOpen, setGoogleOpen] = useState(false);
    const [calendarRevision, setCalendarRevision] = useState(0);
    const [unavailableDetails, setUnavailableDetails] = useState(null);
    const [locationFilter, setLocationFilter] = useState('all');
    const [teamLocations, setTeamLocations] = useState([]);
    const [bookedSlotDialogOpen, setBookedSlotDialogOpen] = useState(false);
    const [bookedSlotAppointment, setBookedSlotAppointment] = useState(null);
    const [tourAppointmentCancelled, setTourAppointmentCancelled] = useState(false);

    // Status filters (replacing category filters)
    const [filteredStatuses, setFilteredStatuses] = useState({
      'pending': true,
      'completed': true,
      'cancelled': true,
    });

    // Therapist filter
    const [selectedTherapistFilter, setSelectedTherapistFilter] = useState('all');

    // Location multi-select popover
    const [locationAnchorEl, setLocationAnchorEl] = useState(null);
    const [therapistAnchorEl, setTherapistAnchorEl] = useState(null);

    // Edit mode for appointment details dialog
    const [isDetailEditMode, setIsDetailEditMode] = useState(false);
    const [editedDetailEvent, setEditedDetailEvent] = useState(null);

    const currentUserId = userDetails?.id;
    const { blocks: calendarBlocks, error: calendarBlocksError, loading: calendarBlocksLoading } = useCalendarBlocks({
      doctors, teamId: currentOrg?.id, therapistId: selectedTherapistFilter,
      locationId: locationFilter, date: selectedDate, view, revision: calendarRevision, disabled: isVisualTour,
    });
    const calendarChanged = () => { setCalendarRevision(value => value + 1); fetchDoctors(); };
    const calendarReturn = new URLSearchParams(location.search).get('calendar');
    const callbackStarted = useRef(false);
    const [calendarCallbackMessage, setCalendarCallbackMessage] = useState('');
    useEffect(() => {
      const params = new URLSearchParams(location.search);
      if ((!params.has('code') || !params.has('state')) && !params.has('error')) return;
      if (callbackStarted.current) return;
      callbackStarted.current = true;
      const cleanUrl = () => { ['code', 'state', 'scope', 'authuser', 'prompt', 'error', 'error_description'].forEach(key => params.delete(key)); navigate({ pathname: location.pathname, search: params.toString() }, { replace: true }); };
      if (params.has('error')) { setCalendarCallbackMessage('Google Calendar connection was not completed.'); cleanUrl(); return; }
      setCalendarCallbackMessage('Completing Google Calendar connection…');
      const callback = { code: params.get('code'), state: params.get('state') };
      cleanUrl();
      calendarRequest('calendar/callback', { method: 'POST', body: callback })
        .then(() => { setCalendarCallbackMessage(''); setCalendarRevision(value => value + 1); setGoogleOpen(true); })
        .catch(error => setCalendarCallbackMessage(error.message));
    }, [location.pathname, location.search, navigate]);

    const selectedDoctor = doctors.find((doctor) => doctor.id === selectedDoctorId);
    const canManageSelectedDoctor = role === 'Admin' || (currentUserId && selectedDoctor?.user_id === currentUserId);

    const tourAppointment = useMemo(() => {
      const start = new Date();
      start.setHours(10, 30, 0, 0);
      const end = new Date(start);
      end.setMinutes(end.getMinutes() + 30);
      return {
        id: 'tour-appointment-alex-morgan',
        title: 'Alex Morgan — Follow-up',
        patient_name: 'Alex Morgan',
        patient_id: 'DEMO-001',
        age: 34,
        gender: 'Female',
        phone_number: '5555 0184',
        phone_code: '+852',
        category: 'Follow-up',
        session_type: 'video',
        appointment_status: tourAppointmentCancelled ? 'cancelled' : 'scheduled',
        start,
        end,
        doctorUserId: currentUserId,
        belongsToCurrentTeamDoctor: true,
        remarks: 'Review progress and agree next steps.',
        isTourDemo: true,
      };
    }, [currentUserId, tourAppointmentCancelled]);

    useEffect(() => {
      if (isVisualTour) setSelectedDate(new Date());
      if (isVisualTour && tourDemoScreen === 'detail' && !tourAppointmentCancelled) {
        setSelectedAppointment(tourAppointment);
        setEditedDetailEvent(tourAppointment);
        setShowAppointmentDetails(true);
      } else if (selectedAppointment?.isTourDemo) {
        // Back/Skip removes only the example dialog, never a real booking.
        setShowAppointmentDetails(false);
        setSelectedAppointment(null);
        setEditedDetailEvent(null);
      }
    }, [isVisualTour, selectedAppointment?.isTourDemo, setSelectedAppointment, setSelectedDate, setShowAppointmentDetails, tourAppointment, tourAppointmentCancelled, tourDemoScreen]);

    const canViewAppointment = useCallback((appointment) => {
      if (!appointment) return false;
      const currentTeamId = currentOrg?.id ?? null;
      const isDoctor = currentUserId && appointment.doctorUserId === currentUserId;
      if (isDoctor) return true;
      if (role === 'Admin') {
        if (currentTeamId && appointment.team_id === currentTeamId) return true;
        return false;
      }
      return false;
    }, [currentOrg, currentUserId, role]);

    // Fetch locations when currentOrg changes
    useEffect(() => {
      const fetchLocations = async () => {
        if (!currentOrg?.id) {
          setTeamLocations([]);
          setLocationFilter('all');
          return;
        }
        const team = allOrgs?.find(org => String(org.id) === String(currentOrg.id));
        if (team && team.locations !== undefined) {
          setTeamLocations(team.locations || []);
        } else {
          try {
            const { supabase } = await import('../../supabaseClient.js');
            const { data, error } = await supabase
              .from('teams')
              .select('locations')
              .eq('id', currentOrg.id)
              .single();
            if (!error && data) {
              setTeamLocations(data.locations || []);
            } else {
              setTeamLocations([]);
            }
          } catch (e) {
            console.error("Failed to fetch locations", e);
            setTeamLocations([]);
          }
        }
        setLocationFilter('all');
      };
      fetchLocations();
    }, [currentOrg, allOrgs]);

    useEffect(() => {
      if (!showAppointmentDetails) {
        setShowPatientCreationOptions(false);
        setSelectedPatientTeamId('');
        setShowCreatePatientConfirm(false);
        setIsDetailEditMode(false);
        setEditedDetailEvent(null);
      }
    }, [showAppointmentDetails]);

    useEffect(() => {
      setShowPatientCreationOptions(false);
      setSelectedPatientTeamId('');
      setShowCreatePatientConfirm(false);
    }, [selectedAppointment]);

    // Initialize appointment form state
    const emptyAppointment = {
      patient_name: '',
      gender: 'Male',
      age: '',
      patient_id: '',
      phone_number: '',
      phone_code: '852',
      chief_complaint: '',
      category: 'First consultation',
      recurring: 'None',
      session_type: 'in_person',
      virtual_link: '',
      remarks: '',
      ai_summary: '',
      appointment_date: new Date(),
      appointment_starttime: new Date(new Date().setHours(9, 0, 0, 0)),
      appointment_endtime: new Date(new Date().setHours(10, 0, 0, 0)),
      doctor_id: selectedDoctorId || '',
      originalDoctorId: null,
    };

    const [appointmentForm, setAppointmentForm] = useState(emptyAppointment);

    const scheduleLocation = useMemo(() => {
      if (locationFilter !== 'all') {
        return teamLocations.find((item) => String(item.id ?? item.name ?? item) === String(locationFilter)) || null;
      }
      return teamLocations[0] || null;
    }, [locationFilter, teamLocations]);

    const calendarBounds = useMemo(() => {
      const hours = workingHoursFor(selectedDate, { location: scheduleLocation });
      const min = new Date(selectedDate);
      const max = new Date(selectedDate);
      min.setHours(hours.closed ? 9 : Math.floor(hours.start / 60), hours.closed ? 0 : hours.start % 60, 0, 0);
      max.setHours(hours.closed ? 18 : Math.floor(hours.end / 60), hours.closed ? 0 : hours.end % 60, 0, 0);
      return { min, max };
    }, [selectedDate, scheduleLocation]);

    // Handle opening new appointment form
    const handleNewAppointment = (event = null, slotInfo = null) => {
      if (doctorsLoading) {
        setAlert('Doctors are still loading. Please try again in a moment.', 'info');
        return;
      }

      if (doctorsLoadError) {
        setAlert('Unable to load doctors. Please refresh and try again.', 'error');
        return;
      }

      if (doctors.length === 0) {
        setAlert('No doctors are configured for this team.', 'error');
        return;
      }

      setHasDoctorChanged(false);

      // A month-cell click arrives as midnight-to-midnight, which used to open the dialog on
      // 12:00 AM - 12:00 AM and made the therapist fix two fields on every single booking.
      // initialSlot tells "this day" apart from "this time"; see appointmentTimes.js.
      const slot = initialSlot(slotInfo);
      const startDate = slot.date;
      const startTime = slot.start;
      // New sessions use the plan's fixed 60-minute duration. The calendar may hand us a
      // 30/45-minute selection, but that must not create a shorter session by accident.
      const endTime = addMinutes(startTime, DEFAULT_SESSION_MINUTES);

      setAppointmentForm({
        ...emptyAppointment,
        appointment_date: startDate,
        appointment_starttime: startTime,
        appointment_endtime: endTime,
        doctor_id: selectedDoctorId || (doctors.length > 0 ? doctors[0].id : ''),
        originalDoctorId: null,
        phone_code: '852',
      });

      setIsEditingAppointment(false);
      setFieldErrors(blankFieldErrors);
      setSubmitError('');
      setShowAppointmentForm(true);
    };

    // Handle editing an existing appointment
    const handleEditAppointment = (appointment) => {
      if (!canViewAppointment(appointment)) return;
      const originalAppointment = appointment.isRecurringInstance
        ? appointments.find(a => a.id === appointment.originalAppointmentId)
        : appointment;

      setHasDoctorChanged(false);

      setAppointmentForm({
        ...(originalAppointment || appointment),
        scribe_patient_id: null,
        link_action: 'preserve',
        appointment_date: new Date(originalAppointment.start || appointment.start),
        appointment_starttime: new Date(originalAppointment.start || appointment.start),
        appointment_endtime: new Date(originalAppointment.end || appointment.end),
        doctor_id: (originalAppointment?.primaryDoctorId ?? appointment.primaryDoctorId ?? appointment.doctor_id) || '',
        originalDoctorId: originalAppointment?.originalDoctorId ?? appointment.originalDoctorId ?? appointment.doctor_id ?? null,
        phone_number: originalAppointment?.phone_number ?? appointment.phone_number ?? '',
        phone_code: originalAppointment?.phone_code ?? appointment.phone_code ?? '852',
        session_type: originalAppointment?.session_type ?? appointment.session_type ?? 'in_person',
        virtual_link: originalAppointment?.virtual_link ?? appointment.virtual_link ?? '',
      });

      setIsEditingAppointment(true);
      setFieldErrors(blankFieldErrors);
      setSubmitError('');
      setShowAppointmentForm(true);
      setShowAppointmentDetails(false);
    };

    // Handle form field changes
    const handleFormChange = (field, value) => {
      if (field === 'doctor_id' && value !== appointmentForm.doctor_id) {
        setHasDoctorChanged(true);
      }
      if (field === 'appointment_starttime') {
        // Keep the session's length when the start moves, or the therapist has to fix two
        // fields to change one and can silently end up with an end before the start.
        setAppointmentForm((prev) => ({ ...prev, ...startChanged(prev, value) }));
      } else if (field === 'phone_code' || field === 'phone_number') {
        // patient_id IS the client's phone (country code + number), hashed on save and
        // matched against the client's MindVault account — see ClientPicker.js. It follows
        // these two fields instead of being typed into an "id" box nobody could fill in.
        setAppointmentForm((prev) => ({
          ...prev,
          ...appointmentFieldsForPhoneChange(prev, field, value),
        }));
      } else {
        setAppointmentForm({
          ...appointmentForm,
          [field]: value,
        });
      }
      if (fieldErrors[field]) {
        setFieldErrors((prev) => ({ ...prev, [field]: false }));
      }
    };

    // One pick fills name, phone code, phone number, gender, age and the derived
    // patient_id, so the therapist never types the same phone number twice.
    const handlePickClient = (fields, client) => {
      setAppointmentForm((prev) => ({ ...prev, ...fields, scribe_patient_id: client.id, link_action: 'resolve' }));
      setFieldErrors((prev) => ({ ...prev, patient_name: false }));
    };

    // Save appointment.
    //
    // The dialog closes on success ONLY. The old version closed it unconditionally, so a
    // rejected write (42501 on every create since the money-path lockdown) threw away
    // everything the therapist had typed and left a toast saying "Failed to save
    // appointment." behind an already-dismissed form. On failure the form is left exactly as
    // it was — appointmentForm is never touched here — and the reason is rendered inline.
    const handleSaveAppointment = async () => {
      // Age is NOT required: the database does not ask for it, the picker can only fill it for
      // a client who already has one on file, and blocking a booking on it turns "schedule a
      // session" into "go and edit the client record first". The client's name is the one
      // thing this row is useless without.
      const requiredFields = ['patient_name'];
      const missing = requiredFields.filter((key) => !String(appointmentForm[key] ?? '').trim());
      if (missing.length) {
        setFieldErrors((prev) => ({
          ...prev,
          ...missing.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
        }));
        setSubmitError('Please complete all required fields.');
        setAlert('Please complete all required fields.', 'error');
        return;
      }

      // An end before the start saved happily and drew a negative block on the calendar; a
      // mistyped year booked a session into the past. Editing stays laxer than creating, so a
      // session that already happened can still be corrected.
      const selectedDoctorForSchedule = doctors.find((doctor) => String(doctor.id) === String(appointmentForm.doctor_id));
      const timeProblem = validateTimes(appointmentForm, {
        isEditing: isEditingAppointment,
        location: scheduleLocation,
        doctor: selectedDoctorForSchedule,
        serverChecksWorkingHours: true, // The booking RPC uses canonical Profile hours in the clinic timezone.
        appointments,
      });
      if (timeProblem) {
        setSubmitError(timeProblem.message);
        setAlert(timeProblem.message, 'error');
        return;
      }

      const { originalDoctorId, ...appointmentPayload } = appointmentForm;
      const doctorIdToPersist =
        hasDoctorChanged || !originalDoctorId
          ? appointmentForm.doctor_id
          : originalDoctorId;

      setSubmitError('');
      setIsSavingAppointment(true);

      let result;
      try {
        result = await saveAppointment({
          ...appointmentPayload,
          doctor_id: doctorIdToPersist,
          phone_code: appointmentForm.phone_code || '852',
        });
      } finally {
        // In a finally so a throw from anywhere below saveAppointment cannot leave the
        // submit button disabled forever with no way out but a page reload.
        setIsSavingAppointment(false);
      }

      if (!result?.ok) {
        setSubmitError(appointmentErrorMessage(result?.error));
        return;
      }

      setHasDoctorChanged(false);
      setShowAppointmentForm(false);
    };

    // Filter appointments based on therapist, location, and status filters
    const filteredAppointments = useMemo(() => {
      const appointmentSource = isVisualTour ? [...appointments, tourAppointment] : appointments;
      return appointmentSource.filter((appointment) => {
        // Therapist filter applies to all appointments (including cross-team)
        if (selectedTherapistFilter !== 'all') {
          if (
            appointment.primaryDoctorId !== selectedTherapistFilter &&
            appointment.doctor_id !== selectedTherapistFilter
          ) {
            return false;
          }
        }

        // Location and status filters only apply to current-team appointments.
        // Cross-team blocks are always shown once they pass the therapist filter.
        if (appointment.belongsToCurrentTeamDoctor) {
          if (locationFilter !== 'all' && appointment.clinic_location) {
            if (String(appointment.clinic_location) !== String(locationFilter)) {
              return false;
            }
          }

          const status = getArrivalStatus(appointment);
          if (!filteredStatuses[status]) {
            return false;
          }
        }

        return true;
      });
    }, [appointments, isVisualTour, tourAppointment, selectedTherapistFilter, locationFilter, filteredStatuses]);

    // Handle toggling a status filter
    const handleStatusFilterToggle = (status) => {
      setFilteredStatuses(prev => ({
        ...prev,
        [status]: !prev[status]
      }));
    };

    // Handle appointment click on calendar
    const handleSelectEvent = (event) => {
      if (event.calendarUnavailable) { setUnavailableDetails(event); return; }
      if (!event.belongsToCurrentTeamDoctor) {
        setSelectedAppointment(event);
        setIsDetailEditMode(false);
        setShowAppointmentDetails(true);
        return;
      }
      if (!canViewAppointment(event)) {
        setBookedSlotAppointment(event);
        setBookedSlotDialogOpen(true);
        return;
      }
      setSelectedAppointment(event);
      setEditedDetailEvent(event);
      setIsDetailEditMode(false);
      setShowAppointmentDetails(true);
    };

    // Handle slot selection on calendar (to create new appointment)
    const handleSelectSlot = (slotInfo) => {
      if (calendarBlocksLoading || calendarBlocksError) return;
      handleNewAppointment(null, slotInfo);
    };

    // Appointment details dialog - edit mode handlers
    const handleDetailEdit = () => {
      setEditedDetailEvent(selectedAppointment);
      setIsDetailEditMode(true);
    };

    const handleDetailCancelEdit = () => {
      setEditedDetailEvent(selectedAppointment);
      setIsDetailEditMode(false);
    };

    const handleDetailSaveEdit = () => {
      if (editedDetailEvent) {
        handleEditAppointment(editedDetailEvent);
      }
    };

    const handleCreatePatientButtonClick = () => {
      if (!allOrgs || allOrgs.length === 0) {
        setAlert('No teams available for client creation.', 'error');
        return;
      }
      setShowPatientCreationOptions((prev) => !prev);
    };

    const handlePatientTeamChange = (event) => {
      setSelectedPatientTeamId(event.target.value);
    };

    const handlePatientCreationRequest = () => {
      if (!selectedPatientTeamId) {
        setAlert('Please select a team before confirming.', 'error');
        return;
      }
      setShowCreatePatientConfirm(true);
    };

    const handleConfirmCreatePatient = async () => {
      if (!selectedAppointment || !selectedPatientTeamId) return;
      setPatientCreationLoading(true);
      const result = await createPatientFromAppointment(
        selectedAppointment,
        selectedPatientTeamId
      );
      setPatientCreationLoading(false);

      if (result?.success) {
        setShowCreatePatientConfirm(false);
        setShowPatientCreationOptions(false);
        setShowAppointmentDetails(false);
        setSelectedPatientTeamId('');
      }
    };

    // Handle appointment deletion
    const handleDeleteAppointment = (appointment) => {
      if (appointment.package_id && !appointment.isTourDemo) {
        setShowAppointmentDetails(false);
        setPackageDeclineTarget({ id: appointment.id, client_name: appointment.patient_name, scope: 'appointment' });
        return;
      }
      setAppointmentToDelete(appointment);
      setCancellationNote('');
      setCancelError('');
      setDeleteConfirmOpen(true);
    };

    // Confirm appointment deletion.
    //
    // Same rule as the save dialog: the confirmation closes only once the cancellation has
    // actually landed. It used to close first and work afterwards inside a setTimeout, so a
    // refused cancel (the unpaid-row 422 from /cancel-refund, which was every
    // therapist-created appointment) looked identical to a successful one apart from a toast,
    // and the cancellation note the therapist had written was thrown away with the dialog.
    const handleConfirmAppointmentDelete = async () => {
      if (appointmentToDelete?.isTourDemo) {
        setDeleteConfirmOpen(false);
        setShowAppointmentDetails(false);
        setTourAppointmentCancelled(true);
        setAlert('Demo appointment cancelled. No data was changed.', 'success');
        setAppointmentToDelete(null);
        setDeleteRecurringOption('single');
        setCancellationNote('');
        return;
      }

      const noteToSave = cancellationNote;

      setCancelError('');
      setIsCancellingAppointment(true);

      try {
        if (appointmentToDelete.isRecurringInstance && deleteRecurringOption === 'single') {
          // A single occurrence of a recurring series is a rendered instance, not a row:
          // nothing is written, so there is nothing that can be refused.
          const updatedAppointments = appointments.filter(
            a => a.id !== appointmentToDelete.id
          );
          setAppointments(updatedAppointments);
          setAlert('Appointment occurrence removed.', 'success');
        } else {
          const targetId = appointmentToDelete.isRecurringInstance
            ? appointmentToDelete.originalAppointmentId
            : appointmentToDelete.id;

          const result = await deleteAppointment(targetId, noteToSave);

          if (!result?.ok) {
            // Dialog stays open, note still typed, reason on screen.
            setCancelError(appointmentErrorMessage(result?.error, { action: 'cancel' }));
            return;
          }

          if (appointmentToDelete.isRecurringInstance || appointmentToDelete.isRecurring) {
            const updatedAppointments = appointments.filter(
              a => a.id !== targetId && a.originalAppointmentId !== targetId
            );
            setAppointments(updatedAppointments);
          }
        }

        setDeleteConfirmOpen(false);
        setShowAppointmentDetails(false);

        await fetchAppointments();
        setAppointmentToDelete(null);
        setDeleteRecurringOption('single');
        setCancellationNote('');
      } finally {
        setIsCancellingAppointment(false);
      }
    };

    // Event style getter based on arrival status
    const eventStyleGetter = (event) => {
      if (event.calendarUnavailable) return { style: { backgroundColor: '#e5e7eb', color: '#374151', border: '1px solid #cbd5e1', borderRadius: 6 } };
      if (!event.belongsToCurrentTeamDoctor) {
        return {
          style: {
            backgroundColor: '#9ca3af',
            borderRadius: '6px',
            opacity: 0.75,
            color: 'white',
            border: 'none',
            display: 'block',
            fontSize: '13px',
            padding: '2px 6px',
          },
        };
      }

      if (!canViewAppointment(event)) {
        return {
          style: {
            backgroundColor: '#9ca3af',
            borderRadius: '6px',
            opacity: 0.75,
            color: 'white',
            border: 'none',
            display: 'block',
            fontSize: '13px',
            padding: '2px 6px',
          },
        };
      }

      const status = getArrivalStatus(event);
      const backgroundColor = arrivalStatusColors[status] || '#14b8a6';

      return {
        style: {
          backgroundColor,
          borderRadius: '6px',
          opacity: 0.9,
          color: 'white',
          border: 'none',
          display: 'block',
          fontSize: '13px',
          padding: '2px 6px',
        },
      };
    };

    // Custom event component for the calendar — memoized to keep a stable reference so
    // react-big-calendar doesn't remount all events on every parent re-render
    const EventComponent = useCallback(({ event }) => {
      if (event.calendarUnavailable) return <Typography variant="caption" sx={{ color: '#374151' }}>Unavailable</Typography>;
      const canView = canViewAppointment(event);
      const status = getArrivalStatus(event);
      const bgColor = arrivalStatusColors[status] || '#14b8a6';

      if (!event.belongsToCurrentTeamDoctor) {
        return (
          <div style={{
            backgroundColor: '#9ca3af',
            borderRadius: '4px',
            padding: '2px 6px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            overflow: 'hidden',
          }}>
            <Typography variant="caption" noWrap sx={{ fontWeight: 'bold', color: 'white', fontSize: '0.7rem', lineHeight: 1.2 }}>
              {event.patient_name}
            </Typography>
            <Typography variant="caption" display="block" noWrap sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.6rem', lineHeight: 1.1 }}>
              Other Team
            </Typography>
          </div>
        );
      }

      if (!canView) {
        const bookedDoctorName = doctors.find(d => d.id === (event.primaryDoctorId || event.doctor_id))?.name;
        return (
          <div style={{
            backgroundColor: '#9ca3af',
            borderRadius: '4px',
            padding: '2px 6px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            overflow: 'hidden',
          }}>
            <Typography variant="caption" noWrap sx={{ fontWeight: 'bold', color: 'white', fontSize: '0.7rem', lineHeight: 1.2 }}>
              Booked
            </Typography>
            {bookedDoctorName && (
              <Typography variant="caption" display="block" noWrap sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.6rem', lineHeight: 1.1 }}>
                {bookedDoctorName}
              </Typography>
            )}
          </div>
        );
      }

      const formatEventTime = (start, end) => {
        return `${moment(start).format('h:mm A')} - ${moment(end).format('h:mm A')}`;
      };

      return (
        <div style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          overflow: 'hidden',
        }}>
          <Typography
            variant="caption"
            noWrap
            sx={{ fontWeight: 'bold', color: 'white', fontSize: '0.7rem', lineHeight: 1.2 }}
          >
            {event.patient_name}
            {event.isRecurringInstance && " \u{1F501}"}
          </Typography>
          {(view === 'day' || view === 'week') && (
            <Typography
              variant="caption"
              display="block"
              noWrap
              sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.65rem', lineHeight: 1.1 }}
            >
              {formatEventTime(event.start, event.end)}
            </Typography>
          )}
        </div>
      );
    }, [view, canViewAppointment, doctors]);

    // Memoize the components object so react-big-calendar never sees a new reference
    const calendarComponents = useMemo(() => ({
      event: EventComponent,
      toolbar: CustomToolbar,
    }), [EventComponent]);

    // Get arrival status icon
    const getArrivalStatusIcon = (status) => {
      const iconSx = { fontSize: 20 };
      switch (status) {
        case 'pending':
          return <ScheduleIcon sx={{ ...iconSx, color: '#14b8a6' }} />;
        case 'completed':
          return <TaskAltIcon sx={{ ...iconSx, color: '#65a30d' }} />;
        case 'cancelled':
          return <CancelIcon sx={{ ...iconSx, color: '#ef4444' }} />;
        default:
          return null;
      }
    };

    // Get recurring label
    const getRecurringLabel = (value) => {
      switch (value) {
        case 'None': return 'None';
        case 'Every day': return 'Every Day';
        case 'Every week': return 'Every Week';
        case 'Every 2 weeks': return 'Every 2 Weeks';
        case 'Every month': return 'Every Month';
        default: return value || 'None';
      }
    };

    // Render appointment form dialog
    const renderAppointmentFormDialog = () => {
      return (
        <Dialog
          open={showAppointmentForm}
          onClose={() => setShowAppointmentForm(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: '12px', maxHeight: '90vh' } }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CalendarMonthIcon sx={{ color: '#14b8a6' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {isEditingAppointment ? 'Edit Appointment' : 'New Appointment'}
              </Typography>
            </Stack>
            <IconButton onClick={() => setShowAppointmentForm(false)} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ pt: 2 }}>
            <Stack spacing={2.5}>
              {/* Why the write failed, in the dialog rather than in a toast: the form is
                  still here with everything the therapist typed, so the message has to be
                  here too, next to the fields they may need to change. */}
              {submitError && (
                <Alert severity="error" onClose={() => setSubmitError('')}>
                  {submitError}
                </Alert>
              )}

              {/* Therapist */}
              <FormControl fullWidth size="small">
                <InputLabel>Therapist</InputLabel>
                <Select
                  value={appointmentForm.doctor_id}
                  onChange={(e) => {
                    const selected = doctors.find((d) => d.id === e.target.value);
                    if (selected && !selected.isAccessible) {
                      setAlert('Only admin users can create or view appointments for other team members.', 'warning');
                      return;
                    }
                    handleFormChange('doctor_id', e.target.value);
                  }}
                  label="Therapist"
                >
                  {doctors.map((doctor) => (
                    <MenuItem
                      key={doctor.id}
                      value={doctor.id}
                      sx={!doctor.isAccessible ? { color: '#9ca3af' } : {}}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!doctor.isAccessible && (
                          <LockOutlinedIcon sx={{ fontSize: 14, color: '#9ca3af', flexShrink: 0 }} />
                        )}
                        <span>{doctor.name}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Client. This one field replaced "Client Name" + a required "Client ID" box
                  that was really the client's phone number typed a second time. Picking a
                  client fills the name, phone, gender and age, and derives patient_id from
                  that phone; typing a name still books someone who has no record yet. */}
              <ClientPicker
                // While editing, the phone fields below are locked (patient_id is the
                // client's identity on an existing booking), so offer no options to pick:
                // the field stays exactly what it was, a free-text client name.
                clients={isEditingAppointment ? [] : clients}
                loading={clientsLoading}
                form={appointmentForm}
                error={fieldErrors.patient_name}
                helperText={fieldErrors.patient_name ? 'Required' : ''}
                onPickClient={handlePickClient}
                onTypeName={(name) => {
                  setAppointmentForm((prev) => ({ ...prev, patient_name: name, scribe_patient_id: null, link_action: 'offline' }));
                  setFieldErrors((prev) => ({ ...prev, patient_name: false }));
                }}
              />

              {/* Gender & Age */}
              <Stack direction="row" spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Gender</InputLabel>
                  <Select
                    value={appointmentForm.gender}
                    onChange={(e) => handleFormChange('gender', e.target.value)}
                    label="Gender"
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth size="small" label="Age" type="number"
                  value={appointmentForm.age}
                  onChange={(e) => handleFormChange('age', e.target.value)}
                  helperText="Filled in from the client record when known"
                />
              </Stack>

              {/* Phone */}
              <Stack direction="row" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 160 }} disabled={isEditingAppointment}>
                  <InputLabel shrink>Country/Region</InputLabel>
                  <Select
                    value={appointmentForm.phone_code || '852'}
                    onChange={(e) => handleFormChange('phone_code', e.target.value)}
                    label="Country/Region"
                    disabled={isEditingAppointment}
                  >
                    {countryCodes.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {country.name} (+{country.code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth size="small" label="Phone Number"
                  value={appointmentForm.phone_number}
                  onChange={(e) => handleFormChange('phone_number', e.target.value)}
                  disabled={isEditingAppointment}
                />
              </Stack>

              {/* Chief Complaint */}
              <TextField
                fullWidth size="small" label="Presenting Concern" multiline rows={2}
                value={appointmentForm.chief_complaint}
                onChange={(e) => handleFormChange('chief_complaint', e.target.value)}
              />

              {/* Category & Session Type */}
              <Stack direction="row" spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={appointmentForm.category}
                    onChange={(e) => handleFormChange('category', e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="First consultation">First Session</MenuItem>
                    <MenuItem value="Existing patient">Follow-up Session</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" disabled={isEditingAppointment}>
                  <InputLabel>Session Type</InputLabel>
                  <Select
                    value={appointmentForm.session_type || ''}
                    onChange={(e) => handleFormChange('session_type', e.target.value)}
                    label="Session Type"
                    disabled={isEditingAppointment}
                  >
                    <MenuItem value="in_person">In Person</MenuItem>
                    <MenuItem value="video">Video</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              {/* Recurring & Meeting Link */}
              <Stack direction="row" spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Recurring</InputLabel>
                  <Select
                    value={appointmentForm.recurring || ''}
                    onChange={(e) => handleFormChange('recurring', e.target.value)}
                    label="Recurring"
                  >
                    <MenuItem value="None">None</MenuItem>
                    <MenuItem value="Every day">Every Day</MenuItem>
                    <MenuItem value="Every week">Every Week</MenuItem>
                    <MenuItem value="Every 2 weeks">Every 2 Weeks</MenuItem>
                    <MenuItem value="Every month">Every Month</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth size="small" label="Meeting Link"
                  value={appointmentForm.virtual_link || ''}
                  onChange={(e) => handleFormChange('virtual_link', e.target.value)}
                />
              </Stack>

              {/* Date & Time */}
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Stack direction="row" spacing={2}>
                  <DatePicker
                    label="Date"
                    disabled={isEditingAppointment}
                    value={appointmentForm.appointment_date}
                    onChange={(date) => handleFormChange('appointment_date', date)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                  <TimePicker
                    label="Start Time"
                    disabled={isEditingAppointment}
                    value={appointmentForm.appointment_starttime}
                    onChange={(time) => handleFormChange('appointment_starttime', time)}
                    ampm={true}
                    minutesStep={5}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                  <TimePicker
                    label="End Time"
                    disabled={!isEditingAppointment}
                    value={appointmentForm.appointment_endtime}
                    onChange={(time) => handleFormChange('appointment_endtime', time)}
                    ampm={true}
                    minutesStep={5}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Stack>
              </LocalizationProvider>

              {/* Remarks */}
              <TextField
                fullWidth size="small" label="Remarks" multiline rows={2}
                value={appointmentForm.remarks}
                onChange={(e) => handleFormChange('remarks', e.target.value)}
              />

              {/* AI Summary */}
              <TextField
                fullWidth size="small" label="AI Summary" multiline rows={3}
                value={appointmentForm.ai_summary}
                onChange={(e) => handleFormChange('ai_summary', e.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button onClick={() => setShowAppointmentForm(false)} sx={{ color: '#6b7280' }}>
              Cancel
            </Button>
            {/* Disabled while the RPC is in flight: a second click would create a second
                appointment for the same slot, and the first one would still be created. */}
            <Button
              onClick={handleSaveAppointment}
              variant="contained"
              disabled={isSavingAppointment}
              startIcon={isSavingAppointment ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              sx={{
                bgcolor: '#14b8a6',
                '&:hover': { bgcolor: '#0d9488' },
                borderRadius: '8px',
                textTransform: 'none',
              }}
            >
              {isEditingAppointment ? 'Save Changes' : 'Create Appointment'}
            </Button>
          </DialogActions>
        </Dialog>
      );
    };

    // Render appointment details dialog
    const renderAppointmentDetailsDialog = () => {
      if (!selectedAppointment) return null;
      const isCrossTeam = !selectedAppointment.belongsToCurrentTeamDoctor;
      if (!isCrossTeam && !canViewAppointment(selectedAppointment)) return null;

      const event = selectedAppointment;
      const status = getArrivalStatus(event);
      const doctorName = doctors.find(d => d.id === (event.primaryDoctorId || event.doctor_id))?.name || 'Unknown';

      return (
        <Dialog
          open={showAppointmentDetails}
          disableAutoFocus={isVisualTour}
          disableEnforceFocus={isVisualTour}
          disableRestoreFocus={Boolean(selectedAppointment?.isTourDemo)}
          onClose={() => setShowAppointmentDetails(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            'data-tour': isVisualTour && tourDemoScreen === 'detail' ? 'demo-appointment-detail' : undefined,
            sx: { borderRadius: '12px', maxHeight: '90vh' },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CalendarMonthIcon sx={{ color: isCrossTeam ? '#9ca3af' : '#14b8a6' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Appointment Details</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              {!isCrossTeam && (
                <>
                  {status !== 'cancelled' && !event.isTourDemo && (
                    <IconButton
                      size="small"
                      onClick={() => handleEditAppointment(selectedAppointment)}
                      sx={{ color: '#14b8a6' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteAppointment(selectedAppointment)}
                    sx={{ color: '#ef4444' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
              <IconButton size="small" onClick={() => setShowAppointmentDetails(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent dividers sx={{ pt: 2 }}>
            <Stack spacing={3}>
              {isCrossTeam && (
                <Box sx={{ bgcolor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem' }}>
                    This appointment belongs to another team. You can view it here but cannot edit it.
                  </Typography>
                </Box>
              )}
              {/* Status badge */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                p: 1.5, bgcolor: '#f9fafb', borderRadius: '8px'
              }}>
                {getArrivalStatusIcon(status)}
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Status</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {arrivalStatusLabels[status] || status}
                  </Typography>
                </Box>
              </Box>

              {/* Cancellation Note */}
              {event.cancellation_note && (
                <Box sx={{ bgcolor: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '8px', p: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#b91c1c', mb: 0.5 }}>
                    Cancellation Note (Visible to Client)
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7f1d1d' }}>
                    {event.cancellation_note}
                  </Typography>
                </Box>
              )}

              {/* Date & Time, Duration */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {moment(event.start).format('MMMM D, YYYY')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Time</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {moment(event.start).format('h:mm A')} - {moment(event.end).format('h:mm A')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <PersonIcon sx={{ fontSize: 14 }} /> Therapist
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{doctorName}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <RepeatIcon sx={{ fontSize: 14 }} /> Recurring
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {getRecurringLabel(event.recurring)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Category</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {event.category === 'First consultation' ? 'First Session' : 'Follow-up Session'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Session Type</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {event.session_type === 'in_person' ? 'In Person' : event.session_type === 'video' ? 'Video' : 'Not specified'}
                  </Typography>
                </Box>
              </Box>

              {/* Presenting Concern */}
              {event.chief_complaint && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Presenting Concern</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{event.chief_complaint}</Typography>
                </Box>
              )}

              {/* Virtual Meeting */}
              {event.session_type === 'video' && event.virtual_link && (
                <Box sx={{ bgcolor: '#eff6ff', border: '1px solid #dbeafe', borderRadius: '8px', p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <VideocamIcon sx={{ color: '#2563eb', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af' }}>Virtual Meeting</Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontFamily: 'monospace', wordBreak: 'break-all', display: 'block', mb: 1 }}>
                    {event.virtual_link}
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<VideocamIcon />}
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    onClick={() => window.open(event.virtual_link, '_blank')}
                    sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' }, textTransform: 'none', borderRadius: '6px' }}
                  >
                    Join Meeting
                  </Button>
                </Box>
              )}

              {/* Patient Info */}
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <PersonIcon sx={{ color: '#14b8a6', fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Client Information</Typography>
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>Name</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{event.patient_name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>Age</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{event.age}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>Gender</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{event.gender}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>Phone</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {event.phone_number
                        ? `+${event.phone_code || '852'} ${event.phone_number}`
                        : 'Not provided'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* AI Summary */}
              {event.ai_summary && (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <AutoAwesomeIcon sx={{ color: '#14b8a6', fontSize: 18 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>AI Session Summary</Typography>
                  </Stack>
                  <Box sx={{ bgcolor: '#f0fdfa', border: '1px solid #ccfbf1', borderRadius: '8px', p: 2 }}>
                    <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.6 }}>
                      {event.ai_summary}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Remarks */}
              {event.remarks && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Notes</Typography>
                  <Typography variant="body2" sx={{ color: '#374151' }}>{event.remarks}</Typography>
                </Box>
              )}

              {/* Create Patient — hidden for cross-team appointments */}
              {/* {!isCrossTeam && <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      bgcolor: '#14b8a6',
                      '&:hover': { bgcolor: '#0d9488' },
                      borderRadius: '6px',
                      textTransform: 'none',
                    }}
                    onClick={handleCreatePatientButtonClick}
                  >
                    Create Patient
                  </Button>
                  {showPatientCreationOptions && (
                    <>
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Organization</InputLabel>
                        <Select
                          value={selectedPatientTeamId}
                          label="Organization"
                          onChange={handlePatientTeamChange}
                        >
                          {allOrgs?.map((org) => (
                            <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handlePatientCreationRequest}
                        disabled={patientCreationLoading}
                        sx={{ borderRadius: '6px', textTransform: 'none' }}
                      >
                        {patientCreationLoading ? <CircularProgress size={18} /> : 'Confirm'}
                      </Button>
                    </>
                  )}
                </Stack>
              </Box>} */}
            </Stack>
          </DialogContent>
        </Dialog>
      );
    };


    return (
        <Container disableGutters maxWidth={false}>
            <AvailabilityDialog open={availabilityOpen} onClose={() => setAvailabilityOpen(false)} onChanged={calendarChanged} doctors={doctors} teamId={currentOrg?.id} userId={currentUserId} isAdmin={role === 'Admin'} />
            <GoogleCalendarDialog open={googleOpen} onClose={() => setGoogleOpen(false)} onChanged={calendarChanged} />
            <Dialog open={Boolean(unavailableDetails)} onClose={() => setUnavailableDetails(null)}><DialogTitle>Unavailable</DialogTitle><DialogContent><Typography>{unavailableDetails?.source === 'google' ? 'Busy in Google Calendar' : 'Time off'}</Typography><Typography>{unavailableDetails?.start?.toLocaleString()} – {unavailableDetails?.end?.toLocaleString()}</Typography></DialogContent><DialogActions><Button onClick={() => setUnavailableDetails(null)}>Close</Button></DialogActions></Dialog>

            <Grid container sx={{overflow: 'auto', minHeight: '100vh', minWidth: '100vw'}}>
                <Grid size={{xs: 1, md: 0}}>
                  <MiniDrawer />
                </Grid>
                <Grid size="grow">
                  <Grid container spacing={5} sx={{ backgroundColor: '#f9fafb', display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
                    <Grid size={12}>
                      <div data-tour="appointment-workspace" style={{ padding: '24px 10vw', paddingBottom: 40 }}>
                        {/* Header */}
                        <Box sx={{ mb: 3 }}>
                          <Stack direction={{ xs: "column", lg: "row" }} alignItems={{ xs: "stretch", lg: "center" }} justifyContent="space-between" spacing={2}>
                            <Box>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                <CalendarMonthIcon sx={{ color: '#14b8a6', fontSize: 24 }} />
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>Appointment Calendar</Typography>
                              </Stack>
                              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                Manage and view all scheduled appointments
                              </Typography>
                            </Box>
                            <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                              <Button variant="outlined" disabled={isVisualTour || doctorsLoading || !(role === 'Admin' || doctors.some(doctor => doctor.user_id === currentUserId))} onClick={() => setAvailabilityOpen(true)}>{role === 'Admin' ? 'Manage availability' : 'My availability'}</Button>
                              <Button variant="outlined" disabled={isVisualTour} onClick={() => setGoogleOpen(true)}>My Google Calendar</Button>
                            <Button
                              onClick={() => handleNewAppointment()}
                              disabled={isVisualTour || doctorsLoading || Boolean(doctorsLoadError) || calendarBlocksLoading || Boolean(calendarBlocksError)}
                              variant="contained"
                              startIcon={<AddIcon />}
                              sx={{
                                bgcolor: '#14b8a6',
                                '&:hover': { bgcolor: '#0d9488' },
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 500,
                                px: 3,
                              }}
                            >
                              New Appointment
                            </Button>
                            </Stack>
                          </Stack>
                        </Box>

                        <Box>
                        {/* Filters Card */}
                        <Box sx={{
                          bgcolor: '#fff',
                          borderRadius: '10px',
                          border: '1px solid #e5e7eb',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                          p: 2.5,
                          mb: 3,
                        }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            {/* Location Filter */}
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                <LocationOnIcon sx={{ fontSize: 16 }} /> Location
                              </Typography>
                              <FormControl fullWidth size="small">
                                <Select
                                  value={locationFilter}
                                  onChange={(e) => setLocationFilter(e.target.value)}
                                  displayEmpty
                                  sx={{ borderRadius: '8px' }}
                                >
                                  <MenuItem value="all">All Locations</MenuItem>
                                  {teamLocations && teamLocations.length > 0 ? (
                                    teamLocations.map((loc) => (
                                      <MenuItem key={loc.id} value={loc.id}>{loc.address}</MenuItem>
                                    ))
                                  ) : (
                                    <MenuItem disabled value="none">
                                      <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                        No locations set for this organization
                                      </Typography>
                                    </MenuItem>
                                  )}
                                </Select>
                              </FormControl>
                            </Box>

                            {/* Therapist Filter */}
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                <PersonIcon sx={{ fontSize: 16 }} /> Therapist
                              </Typography>
                              <FormControl fullWidth size="small">
                                <Select
                                  value={selectedTherapistFilter}
                                  onChange={(e) => {
                                    const selected = doctors.find((d) => d.id === e.target.value);
                                    if (selected && !selected.isAccessible) {
                                      setAlert('Only admin users can view other team members\' appointments.', 'warning');
                                      return;
                                    }
                                    setSelectedTherapistFilter(e.target.value);
                                  }}
                                  displayEmpty
                                  sx={{ borderRadius: '8px' }}
                                >
                                  <MenuItem value="all">All Therapists</MenuItem>
                                  {doctors.map((doctor) => (
                                    <MenuItem
                                      key={doctor.id}
                                      value={doctor.id}
                                      sx={!doctor.isAccessible ? { color: '#9ca3af' } : {}}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {!doctor.isAccessible && (
                                          <LockOutlinedIcon sx={{ fontSize: 14, color: '#9ca3af', flexShrink: 0 }} />
                                        )}
                                        <span>{doctor.name}</span>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          </Box>

                          {/* Status Legend */}
                          <Box sx={{ borderTop: '1px solid #e5e7eb', pt: 2 }}>
                            <Stack direction="row" spacing={2.5} flexWrap="wrap" useFlexGap>
                              {Object.entries(arrivalStatusColors).map(([status, color]) => (
                                <Stack
                                  key={status}
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.75}
                                  onClick={() => handleStatusFilterToggle(status)}
                                  sx={{
                                    cursor: 'pointer',
                                    opacity: filteredStatuses[status] ? 1 : 0.4,
                                    transition: 'opacity 0.2s',
                                    '&:hover': { opacity: filteredStatuses[status] ? 0.8 : 0.6 },
                                  }}
                                >
                                  <Box sx={{
                                    width: 16, height: 16, borderRadius: '4px', bgcolor: color
                                  }} />
                                  <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem' }}>
                                    {arrivalStatusLabels[status]}
                                  </Typography>
                                </Stack>
                              ))}
                            </Stack>
                          </Box>

                          {/* Edit Rules Button - commented out per user request */}
                          {/* <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              onClick={handleEditRulesClick}
                              variant="outlined"
                              sx={{ borderRadius: '8px', textTransform: 'none', color: '#14b8a6', borderColor: '#14b8a6' }}
                            >
                              Edit Rules
                            </Button>
                          </Box> */}
                        </Box>

                        {calendarCallbackMessage && <Alert severity="info" sx={{ mb: 2 }}>{calendarCallbackMessage}</Alert>}
                        {calendarReturn && <Alert sx={{ mb: 2 }} severity={calendarReturn === 'connected' ? 'success' : 'error'} action={<Button color="inherit" onClick={() => setGoogleOpen(true)}>Calendar settings</Button>}>{calendarReturn === 'connected' ? 'Google Calendar connected. Choose your calendars in My Google Calendar.' : 'Google Calendar could not connect. Open My Google Calendar to try again.'}</Alert>}
                        {calendarBlocksLoading && <Typography variant="body2" sx={{ mb: 2 }}>Checking availability…</Typography>}
                        {calendarBlocksError && <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => setCalendarRevision(value => value + 1)}>Retry</Button>}>Unavailable times could not be loaded. {calendarBlocksError}</Alert>}
                        {/* Calendar Card */}
                        <Box sx={{
                          bgcolor: '#fff',
                          borderRadius: '10px',
                          border: '1px solid #e5e7eb',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                          p: 3,
                          minHeight: '700px',
                        }}>
                          <Box data-tour={isVisualTour && tourDemoScreen === 'calendar' ? 'demo-appointment-card' : undefined}>
                          <Calendar
                            localizer={localizer}
                            events={[...filteredAppointments, ...calendarBlocks]}
                            startAccessor="start"
                            endAccessor="end"
                            style={{ height: '100%', minHeight: '650px' }}
                            views={['month', 'week', 'day']}
                            view={view}
                            onView={setView}
                            onNavigate={setSelectedDate}
                            date={selectedDate}
                            min={calendarBounds.min}
                            max={calendarBounds.max}
                            onSelectEvent={handleSelectEvent}
                            onSelectSlot={isVisualTour ? undefined : handleSelectSlot}
                            selectable={!isVisualTour && !calendarBlocksLoading && !calendarBlocksError}
                            components={calendarComponents}
                            eventPropGetter={eventStyleGetter}
                            step={30}
                            timeslots={1}
                            formats={{
                              timeGutterFormat: (date, culture, localizer) =>
                                localizer.format(date, 'HH:mm', culture),
                            }}
                            popup={true}
                            popupOffset={10}
                            length={7}
                            showMultiDayTimes={true}
                          />
                          </Box>
                        </Box>
                        </Box>
                      </div>
                    </Grid>
                  </Grid>
                </Grid>

                {loading && (
                  <Box sx={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(255,255,255,0.75)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9998 }}>
                    <Loader />
                  </Box>
                )}

                {packageDeclineTarget && <DeclinePackageDialog target={packageDeclineTarget} onClose={closePackageDecline} onSuccess={closePackageDecline} />}
                {/* Appointment Form Dialog */}
                {renderAppointmentFormDialog()}

                {/* Appointment Details Dialog */}
                {showAppointmentDetails && renderAppointmentDetailsDialog()}

                {/* Create Patient Confirmation Dialog */}
                <Dialog
                  open={showCreatePatientConfirm}
                  onClose={() => setShowCreatePatientConfirm(false)}
                  PaperProps={{ sx: { borderRadius: '12px' } }}
                >
                  <DialogTitle>Confirm client creation</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      Are you sure you want to create a new client record for{' '}
                      {selectedAppointment?.patient_name} in this team?
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setShowCreatePatientConfirm(false)} disabled={patientCreationLoading}>
                      Cancel
                    </Button>
                    <Button onClick={handleConfirmCreatePatient} color="primary" autoFocus disabled={patientCreationLoading}>
                      {patientCreationLoading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : 'Confirm'}
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Booked Slot Info Dialog */}
                <Dialog
                  open={bookedSlotDialogOpen}
                  onClose={() => setBookedSlotDialogOpen(false)}
                  maxWidth="xs"
                  fullWidth
                  PaperProps={{ sx: { borderRadius: '12px' } }}
                >
                  <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <LockOutlinedIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>Slot Booked</Typography>
                    </Stack>
                    <IconButton size="small" onClick={() => setBookedSlotDialogOpen(false)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent dividers sx={{ pt: 2 }}>
                    {bookedSlotAppointment && (
                      <Stack spacing={2}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Therapist</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {doctors.find(d => d.id === (bookedSlotAppointment.primaryDoctorId || bookedSlotAppointment.doctor_id))?.name || 'Unknown'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Date</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {moment(bookedSlotAppointment.start).format('MMMM D, YYYY')}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>Time</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {moment(bookedSlotAppointment.start).format('h:mm A')} – {moment(bookedSlotAppointment.end).format('h:mm A')}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ bgcolor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '8px', p: 1.5 }}>
                          <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem' }}>
                            This slot is booked by a colleague. Client details are not visible to protect privacy.
                          </Typography>
                        </Box>
                      </Stack>
                    )}
                  </DialogContent>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                  open={deleteConfirmOpen}
                  onClose={() => setDeleteConfirmOpen(false)}
                  PaperProps={{ sx: { borderRadius: '12px', minWidth: '350px' } }}
                >
                  <DialogTitle>
                    {appointmentToDelete ? "Cancel Appointment" : "Delete Item"}
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      {appointmentToDelete
                        ? `Are you sure you want to cancel the appointment for ${appointmentToDelete.patient_name}?`
                        : "Are you sure you want to delete this item?"
                      }
                    </DialogContentText>

                    {appointmentToDelete && appointmentToDelete.isRecurringInstance && (
                      <FormControl component="fieldset" sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">This is a recurring appointment.</Typography>
                        <RadioGroup
                          value={deleteRecurringOption}
                          onChange={(e) => setDeleteRecurringOption(e.target.value)}
                        >
                          <FormControlLabel
                            value="single"
                            control={<Radio />}
                            label="Cancel only this occurrence"
                          />
                          <FormControlLabel
                            value="all"
                            control={<Radio />}
                            label="Cancel this and all future occurrences"
                          />
                        </RadioGroup>
                      </FormControl>
                    )}

                    {appointmentToDelete && (
                      <TextField
                        margin="dense"
                        label="Cancellation Note (Visible to Client)"
                        type="text"
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        value={cancellationNote}
                        onChange={(e) => setCancellationNote(e.target.value)}
                        placeholder="Provide a cancellation note for the client to see on Mindvault..."
                        sx={{ mt: 2.5 }}
                      />
                    )}

                    {/* Same reasoning as the form dialog: the note is still typed in the
                        field above, so the reason the cancellation was refused belongs
                        here, not in a toast behind the dialog. */}
                    {cancelError && (
                      <Alert severity="error" sx={{ mt: 2 }} onClose={() => setCancelError('')}>
                        {cancelError}
                      </Alert>
                    )}
                  </DialogContent>
                  <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ color: '#6b7280' }}>Cancel</Button>
                    <Button
                      onClick={handleConfirmAppointmentDelete}
                      color="error"
                      variant="contained"
                      disabled={isCancellingAppointment}
                      startIcon={isCancellingAppointment ? <CircularProgress size={16} color="inherit" /> : null}
                      autoFocus
                    >
                      {appointmentToDelete ? "Cancel Appointment" : "Delete"}
                    </Button>
                  </DialogActions>
                </Dialog>
            </Grid>

            {/* Profile Setup Required overlay */}
            {(subscriptionActiveStatus !== false && userDetails && !userDetails.mode) && (
              <Box
                sx={{
                  position: 'fixed', top: 0, left: 0,
                  width: '100vw', height: '100vh',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  display: 'flex', justifyContent: 'center', alignItems: 'center',
                  zIndex: 9999,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '40px',
                    maxWidth: '500px', width: '90%', textAlign: 'center',
                    boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.2)', border: '2px solid #14b8a6',
                  }}
                >
                  <Box sx={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#f0fdfa', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto 20px auto' }}>
                    <Typography sx={{ fontSize: '36px', color: '#14b8a6' }}>&#9881;&#65039;</Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333333', marginBottom: '16px' }}>
                    Profile Setup Required
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#666666', marginBottom: '32px', lineHeight: 1.6 }}>
                    Your profile setup is incomplete. To continue using the application, please select your User Mode in Profile Settings.
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{ backgroundColor: '#14b8a6', color: '#FFFFFF', borderRadius: '30px', padding: '12px 24px', textTransform: 'none', fontWeight: 'bold', '&:hover': { backgroundColor: '#0d9488' } }}
                    onClick={() => navigate('/settings')}
                  >
                    Go to Settings
                  </Button>
                </Box>
              </Box>
            )}

            {/* Subscription Inactive overlay */}
            {subscriptionActiveStatus === false && (
              <Box
                sx={{
                  position: 'fixed', top: 0, left: 0,
                  width: '100vw', height: '100vh',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  display: 'flex', justifyContent: 'center', alignItems: 'center',
                  zIndex: 9999,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '40px',
                    maxWidth: '500px', width: '90%', textAlign: 'center',
                    boxShadow: '0px 12px 32px rgba(0, 0, 0, 0.2)', border: '2px solid #FF6B6B',
                  }}
                >
                  <Box sx={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#FFE6E6', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto 20px auto' }}>
                    <Typography sx={{ fontSize: '36px', color: '#FF6B6B' }}>&#9888;&#65039;</Typography>
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333333', marginBottom: '16px' }}>
                    Subscription Inactive
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#666666', marginBottom: '32px', lineHeight: 1.6 }}>
                    Your subscription has expired and access to our platform is currently suspended.
                    To continue using our services and manage your clients, please reactivate your subscription from the settings.
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{ backgroundColor: '#14b8a6', color: '#FFFFFF', borderRadius: '30px', padding: '12px 24px', textTransform: 'none', fontWeight: 'bold', '&:hover': { backgroundColor: '#0d9488' } }}
                    onClick={() => navigate('/settings')}
                  >
                    Subscribe Now
                  </Button>
                </Box>
              </Box>
            )}
        </Container>
    );
};

export default Appointments;
